// src/auth/roles.ts
import type { UserData } from './types';

// Verbatim from Flutter app_constants.dart.
export const SCRIPS_ADMIN_ROLE = '0';
export const ORG_ADMIN_ROLE = '1';
export const STAFF_ROLE = '2';
export const DOCTOR_ROLE = '3';
export const NURSE_TENANT_ROLE = '4';

// The allied-health clinical access level (PROD-2040). This is NOT in Flutter
// app_constants.dart — it is a new `TenantMasterId` seed value introduced by
// Scrips.Identity #527, so React is its first consumer. It arrives on the
// same wire field as NURSE_TENANT_ROLE: staff accounts (`role === STAFF_ROLE`)
// carrying it as `tenantRoleId`.
//
// ONE id, not one per discipline. Health coach / dietitian / exercise
// physiologist are DISCIPLINES, not access levels: they differ in what they
// treat, not in what the app lets them do. The discipline lives in the
// `speciality` field — see the follow-up note on `clinicalRoleFor` in
// use-role.ts.
export const ALLIED_HEALTH_TENANT_ROLE = '20';

type RoleBits = Pick<UserData, 'role' | 'tenantRoleId' | 'isOnboarded'>;

/** Normalise a role-ish field for comparison. The typed `UserData` says
 *  `string`, but the wire is JSON and has been observed handing these back as
 *  numbers, so coerce; `null`/`undefined` become `''`, which must never equal a
 *  role id. Mirrors the coercion `provider-self-filter.ts` already did locally. */
function roleStr(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

/**
 * Is this user a nurse?
 *
 * `ScripsRole = 2` (STAFF) on a tenant role whose `TenantRoles.TenantMasterId = 4`.
 * That is the ONLY nurse signal the login payload carries, and the check is
 * deliberately STRICT — a blank/missing `tenantRoleId` is NOT a nurse.
 *
 * ## What the wire actually carries
 *
 * `GetUserDataToken` (AccountController.cs:539) serialises the DTO in
 * `Scrips.Identity.Infrastructure/Commands/AccountManagement/GetUserTokenData/`:
 *
 *   - `UserDataResult.cs:23-61` — the wire DTO. It has `Role` and `TenantRoleId`
 *     and NOTHING else role-bearing: no `IsNurse` bit, no role name.
 *   - `GenerateUserTokenDataCommandHandler.cs:94` — loads the full `TenantRole`
 *     entity, which DOES carry `IsNurse` and `RoleName`.
 *   - `GenerateUserTokenDataCommandHandler.cs:162` — and then projects only:
 *       `TenantRoleId = role != null ? role.TenantMasterId.ToString() : user.RoleInTenant`
 *     `IsNurse` and `RoleName` are read into memory server-side and discarded.
 *
 * So the client has no second nurse signal to fall back on. `TenantMasterId` is
 * `int?` and `Nullable<int>.ToString()` is the EMPTY STRING when null, so a role
 * with a null `TenantMasterId` arrives as `tenantRoleId: ""` — indistinguishable
 * from any other staff role with a null `TenantMasterId`.
 *
 * ## Why we do NOT treat blank as nurse
 *
 * It looks tempting: it would "recognise" nurses on a role with a null
 * `TenantMasterId`. It is wrong on both counts.
 *
 * 1. It cannot help the users it appears to help. The token endpoint refuses a
 *    practitioner-app login outright for a STAFF user whose tenant role is not
 *    flagged `IsNurse`:
 *      `isStaff && (!isNurse || tenantRoleUnresolved)` → refuse
 *      (LoginSurfaceDecisionService.cs:120-127, flags built at :101-103;
 *      enforced hard via ScripsTokenValidator.cs:34-38, registered at
 *      Startup.cs:291 — no warn mode, no feature flag). The rule pre-dates that
 *      refactor: the same `TenantId == Staff && !role.IsNurse` refusal for
 *      client `Scrips.Provider` sits at ScripsTokenValidator.cs:90-98 in the
 *      commit before it. Both this app and the legacy Flutter provider app
 *      resolve to `AppFamily.Practitioner` (AppSurfaceMap.cs:59-68), so the rule
 *      covers both. A staff user without `IsNurse` therefore never obtains a
 *      token, never reaches `GetUserDataToken`, and never runs this function.
 *      No client-side change can reach them.
 * 2. It would create real false positives. `TenantRoles.IsNurse` is not "this
 *    person is a nurse" — it is the practice-vs-practitioner app router for
 *    staff, and allied-health roles (health coach, dietitian, exercise
 *    physiologist) are being given `IsNurse = 1` for exactly that routing
 *    reason. Those users DO log into this app, are NOT nurses, and would be
 *    misclassified by a blank-means-nurse fallback.
 *
 * ## Error direction
 *
 * Strict matching errs toward reading a nurse as a doctor: an unrecognised nurse
 * sees the onboarding gate and an unfiltered provider roster. The opposite
 * error — a non-nurse treated as a nurse — is worse (it skips onboarding, signs
 * without locking, and narrows the provider roster), so the gate fails closed.
 *
 * ## The real fix is server-side and/or data-side, not here
 *
 * Either set `IsNurse = 1` (and/or `TenantMasterId = 4`) on the affected role
 * rows — a database write — or project `role.IsNurse`, already loaded at handler
 * line 94 and already exposed on `TenantRoleDto.cs:19`, onto the `UserData` DTO.
 * If that field ever lands here, note that its server meaning is "clinical staff
 * who belongs in the practitioner app", NOT "is a nurse"; the two diverge as
 * soon as allied-health personas ship, so it must not be wired straight into
 * this function without a separate nurse signal.
 */
export function isNurse(u: RoleBits | null | undefined): boolean {
  if (!u) return false;
  return roleStr(u.role) === STAFF_ROLE && roleStr(u.tenantRoleId) === NURSE_TENANT_ROLE;
}

export function isDoctor(u: RoleBits | null | undefined): boolean {
  return roleStr(u?.role) === DOCTOR_ROLE;
}

/**
 * Is this user on the allied-health access level?
 *
 * Keys on `tenantRoleId === ALLIED_HEALTH_TENANT_ROLE` ALONE — deliberately NOT
 * the `role === STAFF_ROLE &&` shape `isNurse` uses, and this is the whole point
 * of the change rather than a loosening by accident.
 *
 * WHY THE `role` HALF HAD TO GO. Allied health is moving tenant bucket: the
 * accounts are being re-seeded onto `TenantId = 3` (the "Doctor" bucket) with
 * `IsNurse = 0`, by a SQL script that runs AFTER this code deploys. At bucket 3
 * the login payload reports `role: '3'`, not `'2'`. With the old shape the
 * STAFF_ROLE half then returns false and EVERY allied-health surface unwinds in
 * one step: `isDoctor()` is true instead, `clinicalRoleFor` falls through to its
 * `'physician'` default, the allied-health plan step never renders, and HPI /
 * ROS / Physical Examination come back — the exact defect #385 fixed — along
 * with the physician-only Assessment & Plan step. The tenant role id is the only
 * field that survives the bucket move, so it is the only field this may key on.
 *
 * The coercion and null-safety are UNCHANGED: `roleStr` still normalises the
 * wire (these arrive on the same JSON fields as the nurse bits and have been
 * observed as numbers), `null`/`undefined` still become `''`, and `''` still
 * never equals a role id — so a blank/missing `tenantRoleId` is still NOT
 * allied health. The gate remains STRICT and still fails closed.
 *
 * What is given up by dropping the `role` half is the guarantee that a non-staff
 * account cannot inherit this persona through the tenant id alone. That is
 * accepted knowingly, because after the move the allied-health population IS
 * non-staff: `ALLIED_HEALTH_TENANT_ROLE` is a dedicated `TenantMasterId` seeded
 * for this one access level (Scrips.Identity #527) and is not reused by any
 * other bucket, so the id by itself identifies the persona.
 *
 * This says nothing about WHICH allied-health discipline the user practises —
 * that is `speciality`, not a role.
 */
export function isAlliedHealth(u: RoleBits | null | undefined): boolean {
  return roleStr(u?.tenantRoleId) === ALLIED_HEALTH_TENANT_ROLE;
}

/**
 * THE PHYSICIAN ALLOWLIST (PROD-2669). Samer, 2026-09-25 (administrators confirmed by Andrew):
 * only a physician creates an order (medication, lab, radiology, vaccination) or stops/suspends a
 * prescription. Nurses order nothing (they still reconcile medications); allied health writes plans;
 * phlebotomists, technicians, administrators and receptionists do not order.
 *
 * VERBATIM COPY of Scrips.Common.Core `ClinicianPersona.OrderingPersonaIds` = {3, 8, 18}
 * (Scrips.Common#124, merge 73e3d57, package 9.0.24) — the list Scrips.Patient's order/stop gate
 * and Scrips.VideoApplication's discipline header use. `roles.physician-allowlist.test.ts` quotes
 * that C# declaration and fails if the two lists differ. CHANGE BOTH OR NEITHER.
 *
 *   3  Physician (Space A `DefaultTenantRoles`; "In-house Practitioner" renamed by Identity 007)
 *   8  Visiting Practitioner (Space A)
 *   18 Visiting Practitioner (Space B `TenantMasters`)
 *
 * The ids are `TenantMasterId`s, which is what `GetUserDataToken` projects into `tenantRoleId`
 * (see `isNurse`). `TenantMasterId` carries two id spaces with conflicting meanings (Identity
 * `006-allied-health-roles.sql`): 6 is Billing Staff where production users sit, so it is NOT a
 * physician here; normalising the spaces is PROD-2042. A blank `tenantRoleId` (a role row with no
 * `TenantMasterId`) is not a physician — the backend refuses that caller too.
 *
 * AN ALLOWLIST ON PURPOSE: a persona Identity adds later gets no order controls until someone
 * decides it is a physician. This is the UI's half; the server gates are the authority.
 */
export const ORDERING_PERSONA_IDS: readonly string[] = Object.freeze(['3', '8', '18']);

/** May this user create an order (medication, lab, imaging, procedure, referral, vaccination)? */
export function mayOrder(u: RoleBits | null | undefined): boolean {
  return ORDERING_PERSONA_IDS.includes(roleStr(u?.tenantRoleId));
}

/** May this user stop or suspend a prescription? The same set as `mayOrder` today. */
export function mayStopOrSuspendPrescription(u: RoleBits | null | undefined): boolean {
  return mayOrder(u);
}

/** Flutter getInitialRoute gate: nurses skip onboarding; doctors gate on isOnboarded. */
export function needsOnboarding(u: RoleBits | null | undefined): boolean {
  if (!u) return false;
  if (isNurse(u)) return false;
  return u.isOnboarded !== true;
}
